import type { SetFormApiFn } from '../../form-hooks/form-components/FormStateHandler/FormStateHandler';
import type {
  ContentObject,
  ContentTypeDefinition,
  WithoutInternalProps,
} from '../../lib/flotiq-client/types/api-response';
import type { ContentObjectGetCustomPropsFn } from '../ContentObjectForm/ContentObjectForm';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import deepEqual from 'deep-equal';
import { twMerge } from 'tailwind-merge';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { deepReadKeyValue } from '../../lib/helpers';

import ContentObjectForm from '../ContentObjectForm/ContentObjectForm';

interface VersionContentObjectFormProps {
  /**
   * Content type for object
   */
  contentType: ContentTypeDefinition | null;
  /**
   * Old version data
   */
  oldContentObject?: ContentObject;
  /**
   * Current version data
   */
  currentContentObject?: WithoutInternalProps<ContentObject>;
  /**
   * If form data is current content object
   */
  isCurrent?: boolean;
  /**
   * If tab with the form is selected
   */
  selected?: boolean;
  /**
   * Function to set form state
   */
  setFormState?: SetFormApiFn;
}

const VersionContentObjectForm = ({
  oldContentObject,
  currentContentObject,
  contentType,
  isCurrent = false,
  selected = false,
  setFormState = EMPTY_FUNCTION,
}: VersionContentObjectFormProps) => {
  const { t } = useTranslation();
  const [additionalFieldsCount, setAdditionalFieldsCount] = useState(0);

  const getCustomFieldProps = useCallback<ContentObjectGetCustomPropsFn>(
    (key, props) => {
      const oldValue = deepReadKeyValue(key, oldContentObject);
      const newValue = deepReadKeyValue(key, currentContentObject);
      const isSame = deepEqual(oldValue, newValue);

      const helpText = isSame ? (
        props.helpText
      ) : (
        <>
          {props.helpText}
          <span
            className={twMerge(
              'absolute right-0 -top-2 text-base bg-white dark:bg-slate-900 rounded-xs p-1',
              !isSame
                ? 'text-red-600 dark:text-red-400'
                : 'text-lime-700 dark:text-lime-200',
              isCurrent && 'md:hidden',
            )}
          >
            {t('Versions.Changed')}
          </span>
        </>
      );

      return {
        helpText,
        disabled: isSame,
        readonly: !isSame,
        additionalClasses: '',
      };
    },
    [currentContentObject, isCurrent, oldContentObject, t],
  );

  const contentObject = useMemo(
    () => (isCurrent ? currentContentObject : oldContentObject),
    [currentContentObject, isCurrent, oldContentObject],
  );

  const fieldsCount = useMemo(
    () =>
      Object.values(contentType?.metaDefinition?.propertiesConfig || {}).filter(
        (props) => !props.hidden,
      ).length || Object.keys(currentContentObject || {}).length,
    [contentType?.metaDefinition?.propertiesConfig, currentContentObject],
  );

  return (
    <div
      className={twMerge('md:grid grid-rows-subgrid', selected && 'hidden')}
      style={{
        gridRow: `span ${fieldsCount + additionalFieldsCount}`,
      }}
    >
      <ContentObjectForm
        contentObject={contentObject || {}}
        contentType={contentType}
        disabledBuildInValidation
        formUniqueKey={`${
          contentObject?.internal?.latestVersion || 'current'
        }-version-diff`}
        additionalFormClasses="grid row-span-full grid-rows-subgrid cursor-not-allowed min-w-0"
        getCustomFieldProps={getCustomFieldProps}
        setAddedFieldsCount={setAdditionalFieldsCount}
        setFormState={setFormState}
      />
    </div>
  );
};

export default VersionContentObjectForm;
